// askSmart-ui/src/pages/TOSP/components/LeaseReview/leaseReviewFields.ts

// What the admin walks through when reviewing a lease, in the order the terms
// generally appear in one: the dates, then the term, then the rent, then the
// percentage-rent mechanics, then the clauses.
//
// Each entry names a field on the occupancy form, so finishing the review
// writes straight onto the draft — the review and the form can't drift apart,
// because the review IS the form's fields in a different order.

/** Where a value sits in the PDF, so the page can be scrolled to and marked. */
export interface LeaseAnchor {
  /** 1-based. */
  page: number;
  /**
   * Fractions of the page's width and height, not pixels — the pane renders at
   * whatever scale fits, and a highlight in pixels would land somewhere else
   * at any other width.
   */
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ExtractedValue {
  /** As it will go into the field — already formatted for its kind. */
  value: string;
  /** 0–1 where the extractor reports one. Absent means "no idea". */
  confidence?: number;
  /**
   * What the reviewer should know about how this was read — which area of a
   * rent schedule it came from, or that a clause may govern extensions rather
   * than the initial term. A low confidence says a value is doubtful; this
   * says what to look at to settle it.
   */
  note?: string;
  /** Absent when the extractor found the value but not where it came from. */
  anchor?: LeaseAnchor;
}

/** Keyed by field key. Empty until extraction is built. */
export type LeaseFieldExtraction = Record<string, ExtractedValue>;

/**
 * Everything a lease yields: its own terms, and the spaces it demises.
 *
 * Two channels rather than one flat map, because they're answered differently.
 * A term is confirmed against a clause; a space has to be recognised as a
 * space first, then matched to a unit — and a lease demises however many it
 * demises, so they can't be a fixed list of keys.
 */
export interface LeaseExtraction {
  fields: LeaseFieldExtraction;
  spaces: DetectedSpace[];
}

export type FieldKind = "date" | "text" | "money" | "percent" | "month" | "textarea";

export interface LeaseReviewField {
  /** The draft key this writes to, on whichever record its scope names. */
  key: string;
  label: string;
  kind: FieldKind;
  /**
   * Which record the value belongs to.
   *
   * Three, because a lease states three kinds of thing. The names it's signed
   * under belong to the tenant — one company holding three leases doesn't have
   * three legal entities. The term, the clauses and the deposit belong to the
   * agreement. And the rent belongs to each area the agreement demises, which
   * is why those fields are asked once per leased space rather than once.
   *
   * Defaults to occupancy.
   */
  scope?: "occupancy" | "tenant" | "space";
  /** What the reviewer should be looking for in the lease. */
  hint: string;
}

export const LEASE_REVIEW_FIELDS: LeaseReviewField[] = [
  {
    key: "landlordLegalEntity",
    label: "Landlord Legal Entity",
    kind: "text",
    hint: "The party named as Landlord on the cover page and in the parties clause — not a care-of address in the notice clause.",
  },
  {
    key: "operatingName",
    label: "Operating Name",
    kind: "text",
    scope: "tenant",
    hint: "The name the tenant may trade under, from the Permitted Use and Trade Name clause.",
  },
  {
    key: "legalEntity",
    label: "Legal Entity",
    kind: "text",
    scope: "tenant",
    hint: "The party named as Tenant in the lease — who the agreement is actually with.",
  },
  {
    key: "leaseCommenceDate",
    label: "Lease Commence Date",
    kind: "date",
    hint: "The date the lease itself begins, which may be earlier than the date rent starts.",
  },
  {
    key: "rentCommenceDate",
    label: "Rent Commence Date",
    kind: "date",
    hint: "The date Minimum Rent first becomes payable. Often after a fixturing period.",
  },
  {
    key: "occupancyDate",
    label: "Occupancy Date",
    kind: "date",
    hint: "When the tenant takes possession of the premises.",
  },
  {
    key: "openingDate",
    label: "Opening Date",
    kind: "date",
    hint: "When the tenant is required to open for business.",
  },
  {
    key: "leaseTerm",
    label: "Lease Term",
    kind: "text",
    hint: 'The initial term, e.g. "120 months". Extensions are recorded separately.',
  },
  {
    key: "securityDepositAmount",
    label: "Security Deposit",
    kind: "money",
    hint: "The deposit the lease requires the tenant to lodge — not what was actually paid.",
  },
  {
    key: "extensionTerms",
    label: "Extension Terms",
    kind: "text",
    hint: 'Each renewal period the lease grants, comma separated — e.g. "60 months, 60 months". Sets Date Under Contract.',
  },
  {
    key: "rentRenewalNotations",
    label: "Rent Renewal Notations",
    kind: "textarea",
    hint: "The renewal options: how many, how long, the notice needed, and what the rent resets to.",
  },
  {
    key: "rentPsfCommencement",
    label: "Rent PSF Commencement",
    kind: "money",
    hint: "Minimum Rent per square foot at the start of the term.",
  },
  {
    key: "minimumIncrease",
    label: "Annual Increase Min",
    kind: "percent",
    hint: "The contractual floor on the yearly escalation.",
  },
  {
    key: "maximumIncrease",
    label: "Annual Increase Max",
    kind: "percent",
    hint: "The ceiling, where the lease sets a range rather than a fixed figure.",
  },
  {
    key: "rentStepUpMonth",
    label: "Rent Step Up Month",
    kind: "month",
    hint: "The month the rate steps up each year — usually the anniversary of commencement.",
  },
  {
    key: "percentageRentThresholdRate",
    label: "Breakpoint Rate",
    kind: "percent",
    hint: "The rate the Natural Breakpoint is calculated from.",
  },
  {
    key: "percentageRentPayment",
    label: "Percentage Rent Rate",
    kind: "percent",
    hint: "The rate applied to Gross Revenue above the Natural Breakpoint.",
  },
  // Neither BMVA figure is reviewed here.
  //
  // The royalty is BMVA's own rate, not a negotiated term — every lease states
  // the same number and says it's subject to change, so confirming it against
  // the document proves nothing. It's set once under Configuration → BMVA Fees.
  // The cap is the rare alternative to it and stays a manual entry on the
  // occupancy form rather than a row every review has to clear.
  {
    key: "useClauseSummary",
    label: "Use Clause Summary",
    kind: "textarea",
    hint: "What the premises may be used for, in short.",
  },
  {
    key: "exclusiveSummary",
    label: "Exclusive Summary",
    kind: "textarea",
    hint: "Any exclusivity granted to the tenant, in short.",
  },
];

// What's confirmed once per leased space.
//
// These are the terms that differ from one area of a lease to another: MJ
// Byrne's ground floor, basement, terrace and storage each commence and
// escalate on their own footing under one agreement. Everything above is
// stated once for the whole lease and asked once.
//
// The unit each space is assigned to isn't here. It isn't a value read off the
// document — it's a decision about which TOSP record the space corresponds to,
// so the review asks it as a dropdown on the space rather than as a row to be
// confirmed against a clause.
export const LEASED_SPACE_REVIEW_FIELDS: LeaseReviewField[] = [
  {
    key: "rentCommenceDate",
    label: "Rent Commence Date",
    kind: "date",
    scope: "space",
    hint: "The date Minimum Rent first becomes payable on this space. Often after a fixturing period, and often not the same date for every space.",
  },
  {
    key: "rentPsfCommencement",
    label: "Rent PSF Commencement",
    kind: "money",
    scope: "space",
    hint: "Minimum Rent per square foot for this space at the start of the term.",
  },
  {
    key: "minimumIncrease",
    label: "Annual Increase Min",
    kind: "percent",
    scope: "space",
    hint: "The contractual floor on the yearly escalation for this space.",
  },
  {
    key: "maximumIncrease",
    label: "Annual Increase Max",
    kind: "percent",
    scope: "space",
    hint: "The ceiling, where the lease sets a range rather than a fixed figure.",
  },
  {
    key: "rentStepUpMonth",
    label: "Rent Step Up Month",
    kind: "month",
    scope: "space",
    hint: "The month the rate steps up each year — usually the anniversary of commencement.",
  },
];

/**
 * A space the lease demises, as read out of the document.
 *
 * The reviewer confirms the list and decides which of them correspond to a
 * TOSP unit. Not every one does: a terrace agreement that generates neither
 * rent nor taxes is part of the lease and belongs on the record, but there is
 * no unit to bill it through.
 */
export interface DetectedSpace {
  /** Stable for the life of one review, so rows keep their identity. */
  id: string;
  /** What the lease calls it — "Ground Floor", "Terrace Agreement". */
  name: string;
  /** Why the extractor believes this is a demised space. */
  note?: string;
  anchor?: LeaseAnchor;
  /** True for an area the reviewer added rather than one the rules found. */
  added?: boolean;
  /** The space-scoped values read for it, keyed as above. */
  values: Record<string, ExtractedValue>;
}

/** Namespaces a space's field so one review can hold several of each. */
export const spaceFieldKey = (spaceId: string, key: string): string =>
  `${spaceId}:${key}`;

// The reviewed values, sorted by which record each belongs to.
//
// The review is one list of terms because that's how a lease reads, but they
// land in three places — so the split happens once, here, rather than at each
// caller deciding for itself which keys are the tenant's and which belong to
// which space.
//
// Space values arrive namespaced ("<spaceId>:rentPsfCommencement"), which is
// what lets one flat map of strings carry a lease with four spaces in it.
export function splitReviewValues(values: Record<string, string>): {
  tenant: Record<string, string>;
  occupancy: Record<string, string>;
  /** Keyed by space id, then by field. */
  spaces: Record<string, Record<string, string>>;
} {
  const tenantKeys = new Set(
    LEASE_REVIEW_FIELDS.filter((field) => field.scope === "tenant").map((f) => f.key),
  );
  const tenant: Record<string, string> = {};
  const occupancy: Record<string, string> = {};
  const spaces: Record<string, Record<string, string>> = {};

  for (const [key, value] of Object.entries(values)) {
    const split = key.indexOf(":");
    if (split > 0) {
      const spaceId = key.slice(0, split);
      const field = key.slice(split + 1);
      (spaces[spaceId] ||= {})[field] = value;
      continue;
    }
    (tenantKeys.has(key) ? tenant : occupancy)[key] = value;
  }
  return { tenant, occupancy, spaces };
}

const MONTH_SHORT = "Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec".split(" ");

// A value as the reviewer should read it, rather than as the form stores it.
//
// Only used where two values are put side by side and compared by eye. An ISO
// date against another ISO date is a puzzle — "2021-01-01" and "2009-01-01"
// differ in one character in the middle — where "1 Jan 2021" against "1 Jan
// 2009" is read at a glance. Same for a rate with no % and a rent with no $.
export function displayValue(kind: FieldKind, raw: string): string {
  const value = (raw || "").trim();
  if (!value) return "—";

  if (kind === "date") {
    const [year, month, day] = value.split("-").map(Number);
    const name = MONTH_SHORT[month - 1];
    return year && name && day ? `${day} ${name} ${year}` : value;
  }
  if (kind === "money") return value.startsWith("$") ? value : `$${value}`;
  if (kind === "percent") return value.endsWith("%") ? value : `${value}%`;
  return value;
}

// Whether two entries of the same field say the same thing.
//
// Deliberately forgiving: "$32.50" and "32.5" are the same rent, and "6%" and
// "6" are the same rate. A comparison that called those different would raise
// a conflict on every field and train the reviewer to click past them.
export function sameValue(a: string, b: string): boolean {
  const clean = (raw: string) =>
    (raw || "")
      .trim()
      .toLowerCase()
      // Typographic quotes fold into their plain equivalents: a name read off
      // a lease and the same name typed by an admin differ only in these.
      .replace(/[‘’]/g, "'")
      .replace(/[“”]/g, '"')
      .replace(/[$,%\s]/g, "");
  const left = clean(a);
  const right = clean(b);
  if (left === right) return true;

  const asNumber = (raw: string) => (/^-?\d*\.?\d+$/.test(raw) ? Number(raw) : null);
  const leftNumber = asNumber(left);
  const rightNumber = asNumber(right);
  return leftNumber !== null && rightNumber !== null && leftNumber === rightNumber;
}

/** What the lease has to say about a field, relative to what's on the record. */
export type FieldReviewState =
  /** Nothing extracted — the reviewer keys it in, or leaves it. */
  | { kind: "none" }
  /** The field was blank and the lease offers a value, now filled in. */
  | { kind: "suggested" }
  /** The lease agrees with what was already recorded. */
  | { kind: "agrees" }
  /** The lease disagrees. Never applied on its own — the reviewer chooses. */
  | { kind: "conflict"; recorded: string; suggested: string };

// Reads what it can out of a lease. A field with no rule yet simply isn't in
// the result, which the review shows as "not found" — the same state as a rule
// that ran and found nothing, because to the reviewer they're the same job.
//
// Imported lazily so the rules and the PDF text layer only load when a lease
// is actually opened, rather than with the tenant form.
export async function extractLease(bytes: Uint8Array): Promise<LeaseExtraction> {
  try {
    const [{ readLeaseText }, { runRules }] = await Promise.all([
      import("./leaseText"),
      import("./leaseRules"),
    ]);
    return runRules(await readLeaseText(bytes));
  } catch (err) {
    // A lease that can't be parsed is reviewed by hand — which is what the
    // workspace does anyway when a rule finds nothing.
    console.error("[TOSP] lease extraction failed:", err);
    return { fields: {}, spaces: [] };
  }
}

/**
 * What an amendment (LAA) yields.
 *
 * An amendment isn't a lease — it changes a handful of terms rather than
 * restating them all — so it's read for exactly what it changes, not walked
 * through the full field set. `isAmendment` is the classifier: whether the open
 * document reads as an amendment at all, which is what tells the review to skip
 * the 18-field re-verify.
 */
export interface AmendmentExtraction {
  /** True when the open document reads as an amendment rather than a full lease. */
  isAmendment: boolean;
  /** The date rent commences under the amendment. */
  rentCommenceDate?: ExtractedValue;
  /** The rate per square foot the amendment sets. */
  rentPsfCommencement?: ExtractedValue;
  /** A best-effort summary of other changes, for the Extension Notes box. */
  extensionNotes?: ExtractedValue;
  /** The amendment acknowledges no further options to extend or renew. */
  noFurtherExtensions?: boolean;
}

/**
 * The amendment as it is applied to the occupancy — the reviewed, editable
 * result the workspace hands back, ready to write onto the record.
 */
export interface AmendmentApply {
  /** The leased space the new rent lands on, as a rent reset. */
  spaceId: string;
  /**
   * The renewal this amendment already created, when it is being re-confirmed.
   *
   * Set only when the amendment on screen has been applied before — the admin
   * reopened it to check or correct it. The existing renewal is then updated
   * in place. Absent for a new amendment, which appends one.
   *
   * Without this, confirming the same amendment twice left the lease carrying
   * two identical rent commencements, and neither could be deleted.
   */
  resetId?: string;
  /** ISO yyyy-mm-dd; empty if the reviewer left it blank. */
  rentCommenceDate: string;
  /** Money text; empty if the reviewer left it blank. */
  rentPsfCommencement: string;
  extensionNotes: string;
  /** Mark one extension term used — this renewal exercises an extension. */
  markExtensionUsed: boolean;
}

// Reads what an amendment changes. Parses the same way a lease does, but runs
// the amendment rules — which look for a new rate and date and the prose worth
// carrying, not a schedule of clauses an amendment doesn't have.
/**
 * Below this many characters per page, a document has no usable text.
 *
 * Not a tuning knob so much as a cliff. A real amendment page carries around
 * two thousand characters; the flattened Cheezed LAA carries 38 across two
 * pages, all of it DocuSign stamps. There is nothing in between, so the exact
 * figure hardly matters — it only has to sit in the gap.
 */
const READABLE_CHARS_PER_PAGE = 150;

export async function extractAmendment(bytes: Uint8Array): Promise<AmendmentExtraction> {
  try {
    const [{ readLeaseText }, { runAmendmentRules }] = await Promise.all([
      import("./leaseText"),
      import("./leaseRules"),
    ]);
    const lease = await readLeaseText(bytes);
    const rules = runAmendmentRules(lease);

    // Text won: exact, instant, free. Nothing else to do.
    if (lease.text.trim().length >= READABLE_CHARS_PER_PAGE) return rules;

    // No text worth reading. Not a failure of the rules — the document was
    // flattened to vector outlines when it was made, so there are no glyphs to
    // match and no wording to widen a pattern against. The pixels are the only
    // copy of the words, so they are what gets read.
    const { readAmendmentFromPages } = await import("./amendmentVision");
    const seen = await readAmendmentFromPages(bytes);
    // Falls back to whatever the rules managed rather than to nothing: on a
    // document with a little text, some of it may still be right.
    return seen ?? rules;
  } catch (err) {
    console.error("[TOSP] amendment extraction failed:", err);
    return { isAmendment: false };
  }
}
